import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Settings, Save, Package, Plus, Trash2, Pencil, X, Check, Image as ImageIcon, Upload, Stamp, PenLine } from "lucide-react";
import KaiSettings from "./KaiSettings";
import KaiLibraryPanel from "./KaiLibraryPanel";
import AnaLibraryPanel from "./AnaLibraryPanel";
import AnaProductsSettings from "./AnaProductsSettings";
import MessageTemplatesEditor from "./MessageTemplatesEditor";
import LegalTemplatesEditor from "./LegalTemplatesEditor";
import LookupListsSettings from "./LookupListsSettings";
import RecognitionCentersManager from "./RecognitionCentersManager";
import ConfigDashboardHub from "./ConfigDashboardHub";
import CollapsibleSection from "./CollapsibleSection";
import LinkTtlSettings from "./LinkTtlSettings";
import SignatureMethodSettings from "./SignatureMethodSettings";
import RecaptchaSettings from "./RecaptchaSettings";
import AccountingSectionsSettings from "./AccountingSectionsSettings";
import AccountingAccountsSettings from "./AccountingAccountsSettings";
import PluginsEcosystemPanel from "./PluginsEcosystemPanel";

const AUTOESCUELA_FIELDS = [
  { key: "autoescuela_nombre", label: "Nombre comercial" },
  { key: "autoescuela_num_provincial", label: "Nº provincial DGT" },
  { key: "autoescuela_dgt_numero", label: "DGT — Número" },
  { key: "autoescuela_dgt_digito", label: "DGT — Dígito control" },
  { key: "autoescuela_dgt_seccion", label: "DGT — Sección" },
  { key: "autoescuela_cif", label: "C.I.F." },
  { key: "autoescuela_direccion", label: "Dirección" },
  { key: "autoescuela_tipo_via", label: "Tipo de vía (CALLE, AVENIDA…)" },
  { key: "autoescuela_nombre_via", label: "Nombre de la vía" },
  { key: "autoescuela_numero_via", label: "Número" },
  { key: "autoescuela_bloque", label: "Bloque" },
  { key: "autoescuela_portal", label: "Portal" },
  { key: "autoescuela_escalera", label: "Escalera" },
  { key: "autoescuela_planta", label: "Planta" },
  { key: "autoescuela_puerta", label: "Puerta" },
  { key: "autoescuela_localidad", label: "Localidad" },
  { key: "autoescuela_provincia", label: "Provincia" },
  { key: "autoescuela_codigo_postal", label: "Código postal" },
  { key: "autoescuela_telefono", label: "Teléfono" },
  { key: "autoescuela_director", label: "Director" },
  { key: "autoescuela_director_dni", label: "Director — DNI/NIE" },
  { key: "autoescuela_profesor_default", label: "Profesor asignado (por defecto)" },
  { key: "autoescuela_profesor_dni", label: "Profesor — DNI/NIE" },
  { key: "autoescuela_centro_examen", label: "Centro de examen DGT" },
  { key: "autoescuela_banco", label: "Banco" },
  { key: "autoescuela_cuenta_iban", label: "Cuenta IBAN" },
  { key: "autoescuela_email_lopd", label: "Email LOPD" },
  { key: "autoescuela_empresa_razon_social", label: "Razón social (fiscal)" },
  { key: "autoescuela_empresa_direccion", label: "Dirección fiscal" },
  { key: "a3factura_url", label: "URL de a3factura" },
];

// PNG transparente / firma / sello — se guardan como data URL (base64) en settings.
const AUTOESCUELA_IMAGE_FIELDS = [
  { key: "autoescuela_logo",           label: "Logo",              icon: ImageIcon, help: "Se usa en la cabecera de contratos y documentos DGT internos." },
  { key: "autoescuela_sello",          label: "Sello autoescuela", icon: Stamp,     help: "Se estampa en el Mod.12 (uso interno)." },
  { key: "autoescuela_firma_director", label: "Firma director",    icon: PenLine,   help: "Firma preseleccionada para el Mod.12 (uso interno)." },
  { key: "autoescuela_firma_profesor", label: "Firma profesor",    icon: PenLine,   help: "Firma preseleccionada para el Mod.12 (uso interno)." },
];

async function _fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function ImageSettingCard({ field, value, onChange, saving }) {
  const inputRef = useRef(null);
  const Icon = field.icon || ImageIcon;
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)/i.test(file.type)) {
      toast.error("Formato inválido — usa PNG, JPG o WebP");
      return;
    }
    if (file.size > 1_500_000) {
      toast.error("Imagen demasiado grande (máx 1.5 MB)");
      return;
    }
    try {
      const dataUrl = await _fileToDataUrl(file);
      onChange(dataUrl);
      toast.success(`${field.label} cargado — no olvides guardar`);
    } catch (err) {
      toast.error("No se pudo leer la imagen");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3" data-testid={`setting-image-${field.key}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-4 w-4 text-neutral-600" />
        <span className="text-sm font-semibold">{field.label}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 rounded-lg border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={value} alt={field.label} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[10px] text-neutral-400">Sin imagen</span>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <p className="text-[11px] text-neutral-500 leading-tight">{field.help}</p>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={saving}
              data-testid={`setting-image-upload-${field.key}`}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-700 disabled:opacity-50"
            >
              <Upload className="h-3 w-3" /> Cargar
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                disabled={saving}
                data-testid={`setting-image-clear-${field.key}`}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 disabled:opacity-50"
              >
                <X className="h-3 w-3" /> Quitar
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onPick}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
}

function AutoescuelaSettings() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const allFields = [...AUTOESCUELA_FIELDS, ...AUTOESCUELA_IMAGE_FIELDS];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        allFields.map((f) =>
          api.get(`/settings/${f.key}`).then((r) => [f.key, r.data?.value || ""])
        )
      );
      setValues(Object.fromEntries(results));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(
        allFields.map((f) =>
          api.patch(`/settings/${f.key}`, { value: values[f.key] || "" })
        )
      );
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-neutral-500">Cargando…</div>;

  return (
    <div className="card-surface p-4 space-y-4" data-testid="settings-autoescuela">
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5 text-orange-500" />
        <h3 className="font-bold">Datos de la autoescuela</h3>
      </div>
      <p className="text-xs text-neutral-500">
        Estos datos se usan para generar todos los contratos, anexos y formularios oficiales DGT
        (Mod.04 / Mod.07 / Mod.12).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {AUTOESCUELA_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[11px] font-semibold mb-1 block text-neutral-600">{f.label}</label>
            <input
              type="text"
              value={values[f.key] || ""}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              data-testid={`setting-${f.key}`}
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        ))}
      </div>

      {/* Sección de imágenes corporativas — logo, sello y firmas para Mod.12 */}
      <div className="pt-3 border-t border-neutral-200">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon className="h-4 w-4 text-neutral-700" />
          <h4 className="font-semibold text-sm">Imágenes corporativas (logo, sello y firmas)</h4>
        </div>
        <p className="text-[11px] text-neutral-500 mb-3">
          Recomendado: PNG con fondo transparente. El sello y las firmas se estampan
          automáticamente en el Mod.12 (uso interno).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {AUTOESCUELA_IMAGE_FIELDS.map((f) => (
            <ImageSettingCard
              key={f.key}
              field={f}
              value={values[f.key] || ""}
              onChange={(v) => setValues({ ...values, [f.key]: v })}
              saving={saving}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="settings-save">
          <Save className="h-4 w-4" />
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

const EMPTY_PACK = {
  name: "",
  clases_practicas: 0,
  intentos_teorico: 2,
  intentos_practico: 1,
  matricula: 0,
  tasa_dgt: 0,
  precio_clase: 0,
  precio_pack5: 0,
  total: 0,
  notes: "",
};

function PacksManager() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // pack obj or null
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_PACK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/packs");
      setPacks(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ ...EMPTY_PACK }); setShowAdd(true); setEditing(null); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); setShowAdd(true); };
  const cancel = () => { setShowAdd(false); setEditing(null); setForm(EMPTY_PACK); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      ["clases_practicas", "intentos_teorico", "intentos_practico"].forEach((k) => {
        payload[k] = parseInt(payload[k]) || 0;
      });
      ["matricula", "tasa_dgt", "precio_clase", "precio_pack5", "total"].forEach((k) => {
        payload[k] = parseFloat(payload[k]) || 0;
      });
      if (editing?.id) {
        await api.patch(`/packs/${editing.id}`, payload);
      } else {
        await api.post("/packs", payload);
      }
      toast.success("Pack guardado");
      cancel();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar pack "${p.name}"?`)) return;
    try {
      await api.delete(`/packs/${p.id}`);
      toast.success("Pack eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="card-surface p-4 space-y-3" data-testid="settings-packs">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-orange-500" />
          <h3 className="font-bold">Packs de formación</h3>
        </div>
        {!showAdd && (
          <button onClick={openAdd} className="btn-primary !h-9 !px-3 text-sm" data-testid="packs-new">
            <Plus className="h-4 w-4" /> Nuevo pack
          </button>
        )}
      </div>

      {showAdd && (
        <form onSubmit={save} className="rounded-xl border border-orange-200 bg-orange-50/30 p-3 space-y-3">
          <div className="text-sm font-semibold">{editing ? "Editar pack" : "Nuevo pack"}</div>
          <input
            type="text"
            placeholder="Nombre (ej. Pack Estándar B)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
            data-testid="pack-name"
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["clases_practicas", "Clases prácticas"],
              ["intentos_teorico", "Intentos teórico"],
              ["intentos_practico", "Intentos práctico"],
              ["matricula", "Matrícula (€)"],
              ["tasa_dgt", "Tasa DGT (€)"],
              ["precio_clase", "Precio clase (€)"],
              ["precio_pack5", "Pack 5 (€)"],
              ["total", "TOTAL (€)"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-[11px] font-semibold mb-1 block">{label}</label>
                <input
                  type="number"
                  step="0.01"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`pack-${k}`}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} className="btn-secondary"><X className="h-4 w-4" /> Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="pack-save"><Check className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar"}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-neutral-500">Cargando…</div>
      ) : packs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center text-xs text-neutral-500">
          Aún no hay packs configurados.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="text-left py-2 px-2">Nombre</th>
                <th className="text-right py-2 px-2">Clases</th>
                <th className="text-right py-2 px-2">Teór.</th>
                <th className="text-right py-2 px-2">Práct.</th>
                <th className="text-right py-2 px-2">TOTAL</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {packs.map((p) => (
                <tr key={p.id} className="border-b border-neutral-100" data-testid={`pack-row-${p.id}`}>
                  <td className="py-2 px-2 font-semibold">{p.name}</td>
                  <td className="text-right tabular-nums">{p.clases_practicas}</td>
                  <td className="text-right tabular-nums">{p.intentos_teorico}</td>
                  <td className="text-right tabular-nums">{p.intentos_practico}</td>
                  <td className="text-right tabular-nums font-semibold">{Number(p.total).toFixed(2)} €</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="btn-ghost !h-8 !px-2 text-xs"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(p)} className="btn-ghost !h-8 !px-2 text-xs text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SharedSessionSettings() {
  const [value, setValue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/hide_shared_identity_default");
        setValue((data?.value || "false").toLowerCase() === "true");
      } catch (_) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/settings/hide_shared_identity_default", {
        value: next ? "true" : "false",
      });
      setValue(next);
      toast.success(
        next
          ? "Identidad oculta por defecto en sesiones compartidas"
          : "Identidad visible por defecto en sesiones compartidas"
      );
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  return (
    <div className="card-surface p-4 space-y-3" data-testid="settings-shared-identity">
      <h3 className="font-bold flex items-center gap-2">Sesiones compartidas — identidad</h3>
      <p className="text-xs text-neutral-500">
        Cuando una práctica es compartida, decide si el mensaje de WhatsApp muestra los nombres del resto de participantes. Esta es la política por defecto — cada cita puede sobrescribirla desde la vista previa.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label
          className={
            "flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors " +
            (!value ? "border-emerald-400 bg-emerald-50/70" : "border-neutral-200 bg-white hover:bg-neutral-50")
          }
        >
          <input
            type="radio"
            className="mt-1 accent-emerald-500"
            checked={!value}
            onChange={() => save(false)}
            disabled={saving}
            data-testid="settings-shared-show"
          />
          <div>
            <div className="text-sm font-semibold text-neutral-900">Mostrar identidad</div>
            <div className="text-[11px] text-neutral-500">
              Cada mensaje incluye los nombres, horarios y puntos de recogida de los otros alumnos.
            </div>
          </div>
        </label>
        <label
          className={
            "flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors " +
            (value ? "border-neutral-800 bg-neutral-100" : "border-neutral-200 bg-white hover:bg-neutral-50")
          }
        >
          <input
            type="radio"
            className="mt-1 accent-neutral-900"
            checked={value}
            onChange={() => save(true)}
            disabled={saving}
            data-testid="settings-shared-hide"
          />
          <div>
            <div className="text-sm font-semibold text-neutral-900">Ocultar identidad</div>
            <div className="text-[11px] text-neutral-500">
              Cada mensaje solo indica que la práctica es compartida, sin datos identificativos.
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}


export default function SettingsTab({ onNavigateToTab }) {
  const expandAll = () => window.dispatchEvent(new CustomEvent("collapsibles:expand-all"));
  const collapseAll = () => window.dispatchEvent(new CustomEvent("collapsibles:collapse-all"));
  return (
    <div className="space-y-3 max-w-5xl mx-auto" data-testid="settings-tab">
      <ConfigDashboardHub onNavigateToTab={onNavigateToTab} />
      {/* V1.42.9 · Barra de control para expandir/colapsar todos los bloques
          de golpe (útil para buscar visualmente con Ctrl+F). */}
      <div className="flex items-center justify-end gap-2 px-1">
        <button
          type="button"
          onClick={expandAll}
          className="text-[11px] font-semibold text-neutral-600 hover:text-orange-700 border border-neutral-200 rounded-full px-2.5 h-7 bg-white hover:bg-orange-50 transition-colors"
          data-testid="settings-expand-all"
          title="Abrir todos los bloques (útil para buscar con Ctrl+F)"
        >
          Desplegar todo
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="text-[11px] font-semibold text-neutral-600 hover:text-neutral-900 border border-neutral-200 rounded-full px-2.5 h-7 bg-white hover:bg-neutral-100 transition-colors"
          data-testid="settings-collapse-all"
          title="Colapsar todos los bloques"
        >
          Colapsar todo
        </button>
      </div>
      <CollapsibleSection
        id="settings-kai"
        title="KAI · Supervisor"
        subtitle="Umbrales de detección, escalado y avisos"
      >
        <KaiSettings onNavigateToTray={() => onNavigateToTab && onNavigateToTab("bandeja_kai")} />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-kai-library"
        title="Biblioteca KAI · Plantillas de mensajes"
        subtitle="Editor completo también disponible desde Dashboard → Agentes IA → Biblioteca KAI"
      >
        <KaiLibraryPanel />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-ana-library"
        title="Biblioteca ANA · Plantillas comerciales"
        subtitle="Mensajes comerciales de Ana. Independientes de KAI. Accesibles también desde Dashboard → Comercial · ANA → Biblioteca ANA"
      >
        <AnaLibraryPanel />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-ana-products"
        title="ANA · Productos comerciales"
        subtitle="Catálogo de productos que ofrece Ana en llamadas y WhatsApp"
      >
        <AnaProductsSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-templates"
        title="Plantillas de mensajes"
        subtitle="Textos parametrizables usados en WhatsApp, email y notificaciones"
      >
        <MessageTemplatesEditor />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-lookup-lists"
        title="Listado de valores"
        subtitle="Motivos de ausencia, categorías de gastos y otros catálogos editables"
      >
        <LookupListsSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-signature-method"
        title="Firma digital · Método"
        subtitle="Directa, Email, SMS o Email/SMS — aplica a contratos y DGT"
      >
        <SignatureMethodSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-links-ttl"
        title="Enlaces · Vida útil"
        subtitle="Caducidad de los enlaces firmados enviados por WhatsApp/email"
      >
        <LinkTtlSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-recaptcha"
        title="reCAPTCHA v3"
        subtitle="Protección de acceso — claves y umbrales"
      >
        <RecaptchaSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-accounting-sections"
        title="Contabilidad · Secciones"
        subtitle="Franquicias, centros o líneas de negocio (multi-sección)"
      >
        <AccountingSectionsSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-accounting-accounts"
        title="Contabilidad · Cuentas y Tipos"
        subtitle="Plan contable global · Añade cuentas y tipos personalizados"
      >
        <AccountingAccountsSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-plugins-ecosystem"
        title="Ecosistema de Plugins (SCME)"
        subtitle="Catálogo solo-lectura de módulos reutilizables · SCME v0.2"
      >
        <PluginsEcosystemPanel />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-legal"
        title="Cláusulas legales"
        subtitle="Textos legales usados en contratos y avisos"
      >
        <LegalTemplatesEditor />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-recognition-centers"
        title="Centros de Reconocimiento DGT"
        subtitle="Alta y edición de centros médicos con Nº DGT (auto-relleno)"
      >
        <RecognitionCentersManager />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-autoescuela"
        title="Datos de la autoescuela"
        subtitle="Nombre, CIF, dirección, marca y datos que aparecen en documentos"
      >
        <AutoescuelaSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-shared"
        title="Práctica compartida"
        subtitle="Configuración de sesiones compartidas entre alumnos"
      >
        <SharedSessionSettings />
      </CollapsibleSection>

      <CollapsibleSection
        id="settings-packs"
        title="Packs y facturación"
        subtitle="Paquetes de clases y su precio unitario para facturación automática"
      >
        <PacksManager />
      </CollapsibleSection>
    </div>
  );
}
